import { useCallback } from 'react';

import { type Clip } from '@air/api/types';
import { useAirModal } from '@air/provider-modal';

import { ExportImagesModal } from '~/components/ExportImagesModal/ExportImagesModal';
import { getExportImagesModalPropsForClip } from '~/components/ExportImagesModal/utils/getExportImagesModalPropsForClip';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import {
  type CropAndEditOnCanvasOpenedFrom,
  type GetCropAndEditOnCanvasHandlerParams,
  getCropAndEditOnCanvasHandler,
} from '~/utils/canvases/getCropAndEditOnCanvasHandler';

type PrivateClipExportAsOpenedFrom = Exclude<CropAndEditOnCanvasOpenedFrom, 'asset_modal_export_as'>;

type UseGetPrivateClipExportAsHandlerParams = Pick<
  GetCropAndEditOnCanvasHandlerParams,
  'canCreateCanvas' | 'openClipInCanvas'
>;

type GetPrivateClipExportAsHandlerParams = {
  canDownload: boolean;
  clip: Clip;
  openedFrom: PrivateClipExportAsOpenedFrom;
};

/**
 * Takes `canCreateCanvas` and `openClipInCanvas` from the caller because every menu-options hook
 * already subscribes to them, and those hooks run once per gallery card / table row.
 */
export const useGetPrivateClipExportAsHandler = ({
  canCreateCanvas,
  openClipInCanvas,
}: UseGetPrivateClipExportAsHandlerParams) => {
  const [showExportImagesModal] = useAirModal(ExportImagesModal);
  const { currentWorkspace } = useCurrentWorkspace();

  const getExportAsHandler = useCallback(
    ({ canDownload, clip, openedFrom }: GetPrivateClipExportAsHandlerParams) => {
      if (!canDownload) {
        return undefined;
      }

      const exportAsProps = getExportImagesModalPropsForClip({
        clip,
        workspaceId: currentWorkspace?.id,
      });

      if (!exportAsProps) {
        return undefined;
      }

      return showExportImagesModal.bind(null, {
        ...exportAsProps,
        onCropAndEditOnCanvas: getCropAndEditOnCanvasHandler({
          canCreateCanvas,
          clip,
          openClipInCanvas,
          openedFrom,
        }),
      });
    },
    [canCreateCanvas, currentWorkspace?.id, openClipInCanvas, showExportImagesModal],
  );

  return {
    getExportAsHandler,
  };
};
